import { Link } from "react-router-dom";
import { PLACEHOLDER_IMAGE } from "@/constants/common";
import { CartItemType, type CartItem } from "@/types/cart";
import { formatPrice, stripHTML } from "@/utils/utils";
import { generateProductUrl } from "@/helpers/helpers";
import ProductPrice from "@/components/features/products/ProductPrice";
import CartQuantityControl from "@/components/features/cart/CartQuantityControl";
import SpinnerLoading from "@/components/ui/loading/SpinnerLoading";
import clsx from "clsx";

//#region Types
interface CartItemsProps {
  items: CartItem[] | null;
  updatingStatus?: Record<string, boolean>;
  isUpdating?: boolean;
  quantityControl?: {
    quantityInputs: Record<string, number>;
    handleQuantityChange: (itemKey: string, newQuantity: number) => void;
    handleRemoveCartItem: (itemKey: string) => void;
  };
  showProductLink?: boolean;
}
//#endregion

const CartItems = ({
  items,
  updatingStatus = {},
  isUpdating = false,
  quantityControl = undefined,
  showProductLink = true,
}: CartItemsProps) => {
  if (!items) return null;

  return (
    <section className="brand-card relative">
      <div className="brand-text-xs flex justify-between border-b pb-2">
        <span>商品</span>
        <span>小計</span>
      </div>
      {items.map((item, index) => {
        const { price, sale_price, regular_price } = item.prices;

        return (
          <div
            key={item.id}
            className={clsx("flex flex-row gap-4 border-t py-4", {
              "border-t-0": index === 0,
              "mt-4": index !== items.length - 1,
            })}
          >
            {/* Left Img */}
            {showProductLink ? (
              <Link to={generateProductUrl(item.permalink)}>
                <ProductImage item={item} index={index} />
              </Link>
            ) : (
              <ProductImage item={item} index={index} />
            )}

            {/* Right */}
            <div className="flex flex-col gap-2">
              {/* Product Name */}
              {showProductLink ? (
                <Link to={generateProductUrl(item.permalink)}>
                  <ProductName item={item} isLink={true} />
                </Link>
              ) : (
                <ProductName item={item} />
              )}

              {/* Attribute */}
              {item.type === CartItemType.Variable && item.variation && (
                <div className="brand-text-xs text-base-content">
                  {item.variation.map((variationItem, index) => (
                    <span key={variationItem.attribute} className="mr-2">
                      {`${stripHTML(variationItem.value)}${
                        index < item.variation.length - 1 ? "," : ""
                      }`}
                    </span>
                  ))}
                </div>
              )}

              {/* Price */}
              <ProductPrice
                price={price}
                regular_price={regular_price}
                on_sale={!!sale_price && sale_price < regular_price && price === sale_price}
              />

              {/* Quantity */}
              {quantityControl ? (
                <CartQuantityControl
                  itemKey={item.key}
                  quantity={quantityControl.quantityInputs?.[item.key]}
                  handleQuantityChange={quantityControl.handleQuantityChange}
                  handleRemoveCartItem={() => quantityControl.handleRemoveCartItem(item.key)}
                />
              ) : (
                <span className="text-neutral">x{item.quantity}</span>
              )}
            </div>

            {/* 單項商品小計 */}
            <div className="text-base-conten ml-auto text-right">
              {updatingStatus?.[item.key] ? (
                <SpinnerLoading colorClass="text-neutral" />
              ) : (
                formatPrice(item.totals.line_total)
              )}
            </div>
          </div>
        );
      })}
      {/* 更新時阻止點擊操作 */}
      {isUpdating && <div className="absolute inset-0 z-10" />}
    </section>
  );
};

const ProductImage = ({ item, index }: { item: CartItem; index: number }) => (
  <div className="flex size-24 items-center justify-center overflow-hidden bg-base-200">
    <img
      src={item.images[0].src ?? PLACEHOLDER_IMAGE}
      srcSet={item.images[0].srcset}
      sizes={item.images[0].sizes}
      loading={index === 0 ? "eager" : "lazy"}
      decoding={index === 0 ? "auto" : "async"}
      alt={stripHTML(item.name) || item.images[0].alt || ""}
      className="brand-product-placeholder aspect-[1/1]"
    />
  </div>
);

const ProductName = ({ item, isLink = false }: { item: CartItem; isLink?: boolean }) => (
  <div
    className={clsx("brand-text-sm font-bold text-primary", {
      "brand-link": isLink,
    })}
  >
    {stripHTML(item.name)}
  </div>
);

export default CartItems;
